<template>
  <template v-if="userTeamId">
    <!-- 사용자 id 부분 -->
    <div class="user-id-area">
      <div class="user-box">
        <p>{{ t('organization_id') }}</p>
        <p class="id-name">{{ userTeamId }}</p>
      </div>
    </div>
    <!--추가 사용자 로그인 안내-->
    <div class="container-guide">
      <h4 class="tit-h5">{{ t('ss_user_mx_login_guide') }}</h4>
      <div class="container-loginmethod">
        <div class="wrapper-loginmethod">
          <div class="method01">
            <span class="badge3">{{ t('method_additional_user_login', { method: 1 }) }}</span>
            <p class="mt-3">
              {{ t('ss_user_mx_login_guide_link') }}
            </p>
            <div class="wrapper-link">
              <div class="l-btn qr-link-text">
                <span>{{ urlLoginSub }}</span>
                <button class="copy-btn" :class="{ done: isCopiedUrl }" @click="copyUrlLoginSub">
                  <!-- {{ isCopiedUrl ? t('copy_completed') : t('urlcopy') }} -->
                  <IconCopy class="icon" />
                </button>
              </div>
            </div>
          </div>
          <div class="method02">
            <span class="badge3">{{ t('method_additional_user_login', { method: 2 }) }}</span>
            <div class="mt-3 txt">
              <i18n-t keypath="ss_user_mx_login_guide_alternative">
                <template #buttonName>
                  <span class="accent">{{ t('ss_login_as_sub') }}</span>
                </template>
              </i18n-t>
            </div>
            <div class="inr">
              <img :src="getAssetImageUrl('self-service/guides/user-add2.png')" alt="" />
            </div>
          </div>
        </div>
      </div>

      <button class="add-btn l-btn gray-btn" @click="openCreateView">
        <img :src="getAssetImageUrl('common/icons/round-add.svg')" alt="" />
        {{ t('label_add_user') }}
      </button>
    </div>

    <div class="container-employeetable">
      <!-- 검색 -->
      <InputSearch
        v-model="searchQuery"
        :placeholder="t('alert_search_user_by_id_name')"
        @search="getEmployeeList(true)"
      />

      <TableDynamic
        :moreButtonText="t('label_load_more_user_list')"
        :columns="columns"
        :rows="listUser"
        :showTagFilter="false"
        :show-more-button="hasMore"
        :loading="isLoading"
        @action-hover-click="openUpdateView"
        @more-click="getEmployeeList()"
      />

      <teleport to="body">
        <ModalManageUser
          v-if="['add', 'edit'].includes(pageCurrent)"
          :userInfo="userInfoEdit"
          @close="pageCurrent = ''"
          @refresh="getEmployeeList(true)"
        />
      </teleport>
    </div>
  </template>

  <div v-else class="container-guide">
    <div class="wrapper-title">
      <h4 class="tit-h5">{{ t('ss_mx_user_organization_id_register') }}</h4>
      <!--      <tooltip :tooltip-text="t('organization_id_tooltip')">-->
      <!--        <IconHelp class="icon-small icon-dim" />-->
      <!--      </tooltip>-->
    </div>
    <div class="container-loginmethod">
      <div class="wrapper-loginmethod">
        <p>
          {{ t('ss_mx_user_organization_id_register_info_1') }} <br />
          {{ t('ss_mx_user_organization_id_register_info_2') }}
        </p>
        <div class="wrapper-link mt-4">
          <input type="text" @input="handleTeamId" />
        </div>
        <div class="btn-right-wrap">
          <ButtonProcess :loading="isLoading" :disabled="!isValidTeamId" @click="requestChangeTeamId">
            {{ t('label_register') }}
          </ButtonProcess>
        </div>
        <p v-if="teamId && !isValidTeamId" class="error-message">
          <InfoIcon class="icon-smaller icon-error" />
          {{ t('ss_mx_user_organization_id_error') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { createUserTeam, fetchEmployeeList } from '@/api/apiService';
import ModalManageUser from '@/components/domains/self-service/user-manage/ModalManageUser.vue';
import InfoIcon from '@/components/icons/IconInfo.vue';
import IconCopy from '@/components/jh-app/icons/IconCopy.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import InputSearch from '@/components/shared/base/InputSearch.vue';
import TableDynamic from '@/components/shared/base/TableDynamic.vue';
import { useToastStore } from '@/stores/toast.js';
import { useUserStore } from '@/stores/user';
import { IResponsePartnerEmployee } from '@/types/response';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import { blockInputInvalidId } from '@/utils/validatorInput';

const userStore = useUserStore();
const toastStore = useToastStore();

const router = useRouter();

const { t } = useI18n();

const emit = defineEmits(['open-update', 'open-create']);

const listUser = ref<IResponsePartnerEmployee[]>([]); // 사용자 목록
const hasMore = ref(false); // 더보기 가능 여부
const nextOffset = ref(null); // 다음 오프셋
const searchQuery = ref(''); // 사용자 검색어
const isLoading = ref(false); // 로딩 상태
const pageCurrent = ref(''); // 현재 페이지 (목록 / 추가/ 수정)
const userInfoEdit = ref<IResponsePartnerEmployee>({} as IResponsePartnerEmployee); // 수정할 사용자 정보
const tooltipRef = ref(null);
const tooltipImageRef = ref(null);
const isCopiedUrl = ref(false); // URL 주소가 복사되었는지
const teamId = ref<string>('');

const columns = computed(() => [
  {
    label: t('label_id'),
    key: 'loginId',
    class: 'width30 paddingleft',
  },
  { label: t('label_name'), key: 'nickname', class: 'width60' },
  { type: 'edit', class: 'width3rem' },
]);

// 저장된 팀 아이디
const userTeamId = computed<string>(() => {
  const { teamId = '' } = userStore.userInfo;
  return teamId;
});

const handleTeamId = (e) => {
  blockInputInvalidId(e);
  teamId.value = e.target.value;
};

// 팀아이디 유효성 검사
const isValidTeamId = computed<boolean>(() => {
  return !!teamId.value && teamId.value.length >= 4 && teamId.value.length <= 20;
});

// 서브 계정 로그인 URL 반환
const urlLoginSub = computed(() => {
  const domain = location.origin;
  return `${domain}/login/ss?partnerCode=${userTeamId.value}`;
});

// 사용자 목록 조회
const getEmployeeList = async (refresh = false) => {
  if (isLoading.value === true) return;

  isLoading.value = true;

  // 목록 새로고침일 경우 0부터 조회
  if (refresh) nextOffset.value = null;

  const response = await fetchEmployeeList(nextOffset.value, searchQuery.value, true);
  if (response.success) {
    // 목록 새로고침
    if (refresh) listUser.value = response.data.datas;
    // 더보기
    else listUser.value = [...listUser.value, ...response.data.datas];

    hasMore.value = response.data.hasMore;
    nextOffset.value = response.data.nextOffset;
  } else if (response.errorCode === 4030) {
    await router.push({ name: 'ss-dashboard' });
  }

  isLoading.value = false;
};

// 사용자의 팀 아이디를 등록 요청합니다.
const requestChangeTeamId = async () => {
  if (!isValidTeamId.value) {
    return;
  }

  const response = await createUserTeam(teamId.value);
  if (response.success) {
    await getEmployeeList();
    await userStore.fetchUserInfo();
  } else if (response.errorCode === 1034) {
    toastStore.error(t('error_organization_id_1'));
  } else if (response.errorCode === 1035) {
    toastStore.error(t('error_organization_id_2'));
    await getEmployeeList();
    await userStore.fetchUserInfo();
  }
};

const openCreateView = () => {
  userInfoEdit.value = {} as IResponsePartnerEmployee;
  pageCurrent.value = 'add';
};

// 사용자 수정 클릭 시
const openUpdateView = (employee) => {
  userInfoEdit.value = employee;
  pageCurrent.value = 'edit';
};

// 로그인 URL 복사하기
const copyUrlLoginSub = () => {
  navigator.clipboard.writeText(urlLoginSub.value);
  isCopiedUrl.value = true;
  setTimeout(() => {
    isCopiedUrl.value = false;
  }, 5000);
};

// 이미지 위치
const adjustTooltipPosition = () => {
  const img = tooltipImageRef.value;
  const tooltip = tooltipRef.value;
  if (!img || !tooltip) return;

  // 초기 중앙 기준
  img.style.left = '50%';
  img.style.right = 'auto';
  img.style.transform = 'translateX(-50%)';

  const rect = img.getBoundingClientRect();
  const overflowRight = rect.right > window.innerWidth - 16; // 1rem
  const overflowLeft = rect.left < 16;

  // 🔹 오른쪽이 닿을 때 → 살짝 왼쪽으로 이동
  if (overflowRight) {
    const shift = rect.right - (window.innerWidth - 16);
    img.style.transform = `translateX(calc(-50% - ${shift}px))`;
  }

  // 🔹 왼쪽이 닿을 때 → 살짝 오른쪽으로 이동
  if (overflowLeft) {
    const shift = 16 - rect.left;
    img.style.transform = `translateX(calc(-50% + ${shift}px))`;
  }
};

onMounted(async () => {
  if (userTeamId.value) {
    await getEmployeeList();
  }
  await nextTick(() => adjustTooltipPosition());
  window.addEventListener('resize', adjustTooltipPosition);
});
</script>

<style scoped lang="scss">
.user-id-area {
  padding: 1rem;
  .user-box {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    border: 1px solid $color-gray-40;
    background-color: #fff;
    .id-name {
      font-weight: 600;
    }
  }
}
.user-add-con {
  position: relative;
  overflow-y: auto;
  .footer-wrap {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 100%;
    background: #fff;
    padding: 0.75rem 1rem;
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid $color-gray-40;
    button {
      width: auto;
      padding: 0 1.5rem;
    }
  }
  .wrapper-loginmethod {
    > p {
      word-break: keep-all;
    }
  }
  .wrapper-link {
    width: 100%;
    margin-top: 1.5rem;
    input {
      width: 100%;
    }
  }
}
.container-guide {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  padding: 1.5rem 1rem 2rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid $color-gray-40;

  .wrapper-loginmethod {
    .method01 {
      > p {
        font-size: $font-size-psmall;
      }
      .badge3 {
        color: #0078fb;
        background: #e9f4ff;
        font-size: $font-size-psmall;
      }
    }
    .method02 {
      margin-top: 2.5rem;
      .badge3 {
        color: #c25b00;
        background: #fff7e8;
        font-size: $font-size-psmall;
      }
      .txt {
        font-size: $font-size-psmall;
      }
      .inr {
        border-radius: $size-s;
        overflow: hidden;
        margin-top: 1.5rem;
        img {
          width: 100%;
          object-fit: contain;
          display: block;
        }
      }
    }
  }

  .right {
    text-align: left;
    border-left: 1px solid var(--border-dark);
    padding-left: 2rem;
  }

  .qr-link-text {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    width: fit-content;
    padding: 0.5rem 1rem;
    align-items: center;
    justify-content: space-between;
    text-align: left;
    border-radius: 8px;
    background: var(--surface-default);
    border: 1px solid $color-gray-40;

    width: 100%;
    span {
      display: block;
      width: calc(100% - 32px);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.help-tooltip {
  position: relative;
  display: inline-flex;
  top: 0.25rem;
  cursor: pointer;

  .image-login {
    position: absolute;
    bottom: 120%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.25s ease,
      visibility 0.25s ease;
    width: 18rem;
    max-width: 90vw;
    border-radius: 12px;
    box-shadow: $shadow-dropdown;
    z-index: $z-floating;
  }

  &:hover .image-login {
    opacity: 1;
    visibility: visible;
  }
}

.container-employeetable {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-start;
  position: relative;
  padding: 0 1rem 3rem;
  :deep(.search-container) {
    width: 100%;
    .search-box {
      width: 100%;
    }
  }
}

.wrapper-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error-message {
  width: 100%;
  display: flex;
  justify-content: left;
  align-items: center;
  color: var(--color-red);
  margin-top: 0.25rem;
  gap: 0.25rem;
}
.btn-right-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: $size-m;
  button {
    width: auto;
    background: $color-gray-800;
  }
}
@media (min-width: 768px) {
  .wrapper-search {
    flex-direction: row;
    margin-bottom: unset;
  }
}
</style>
